import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { EntityDialog } from "../components/common/EntityDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SHGs, VOs, CLFs } from "../lib/api";
import { formatIndianDate } from "../lib/format";
import { toast } from "sonner";
import { CONSENT_OPTS, CONSENT_METHODS, validateConsent, WhatsAppBadge } from "../lib/mobile";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";

const ShgsPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [clfs, setClfs] = useState([]);
  const [vos, setVos] = useState([]);
  const [clfFilter, setClfFilter] = useState("all");
  const [voFilter, setVoFilter] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shgsData, vosData, clfsData] = await Promise.all([
        SHGs.list({
          include_inactive: includeInactive,
          ...(voFilter !== "all" ? { vo_id: voFilter } : (clfFilter !== "all" ? { clf_id: clfFilter } : {})),
        }),
        VOs.list({}),
        CLFs.list({}),
      ]);
      setRows(shgsData); setVos(vosData); setClfs(clfsData);
    } catch (e) {
      toast.error(e.message || "Failed to load SHGs");
    } finally { setLoading(false); }
  }, [includeInactive, voFilter, clfFilter]);

  useEffect(() => { load(); }, [load]);

  const voOptions = useMemo(
    () => vos.map((v) => ({ value: v.id, label: `${v.name} (${v.code})` })),
    [vos],
  );
  const voNameById = useMemo(() => { const m = {}; vos.forEach((v) => { m[v.id] = v.name; }); return m; }, [vos]);
  const filteredVoList = useMemo(
    () => clfFilter === "all" ? vos : vos.filter((v) => v.clf_id === clfFilter),
    [vos, clfFilter],
  );

  const fields = [
    { name: "vo_id", label: "Parent VO", type: "select", required: true, options: voOptions, placeholder: "Select VO" },
    { name: "name", label: "SHG name", required: true, placeholder: "e.g. Jagriti Mahila SHG" },
    { name: "code", label: "SHG code", required: true, halfWidth: true, placeholder: "e.g. SHG-001" },
    { name: "village", label: "Village", halfWidth: true },
    { name: "formation_date", label: "Formation date", type: "date", halfWidth: true },
    { name: "contact_person", label: "Contact person", halfWidth: true },
    { name: "phone", label: "Mobile number", type: "mobile", halfWidth: true, placeholder: "10-digit mobile" },
    { name: "whatsapp_consent", label: "WhatsApp consent", type: "select", halfWidth: true, options: CONSENT_OPTS },
    { name: "consent_date", label: "Consent date", type: "date", halfWidth: true, showIf: (v) => v.whatsapp_consent === "yes" },
    { name: "consent_method", label: "How consent was recorded", type: "select", halfWidth: true, options: CONSENT_METHODS, showIf: (v) => v.whatsapp_consent === "yes" },
  ];
  const editFields = [
    ...fields,
    { name: "status", label: "Status", type: "select", halfWidth: true, options: [
      { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
    ] },
  ];

  const handleCreate = async (v) => { await SHGs.create(v); toast.success("SHG created"); await load(); };
  const handleUpdate = async (v) => { await SHGs.update(editing.id, v); toast.success("SHG updated"); await load(); };
  const toggleStatus = async (row) => {
    if (row.status === "active") { await SHGs.deactivate(row.id); toast.success(`${row.name} deactivated`); }
    else { await SHGs.activate(row.id); toast.success(`${row.name} activated`); }
    await load();
  };

  const columns = [
    { key: "code", header: "Code", mono: true, strong: true },
    { key: "name", header: "SHG name", strong: true },
    { key: "vo_id", header: "VO", render: (r) => voNameById[r.vo_id] || "—" },
    { key: "village", header: "Village" },
    { key: "formation_date", header: "Formed on", mono: true, render: (r) => formatIndianDate(r.formation_date) },
    { key: "contact_person", header: "Contact" },
    { key: "phone", header: "Phone", mono: true },
    { key: "whatsapp_consent", header: "WhatsApp", render: (r) => <WhatsAppBadge row={r} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status === "active" ? "Active" : "Closed"} /> },
    { key: "_actions", header: "Actions", render: (r) => (
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" data-testid={`shg-edit-${r.id}`}
          onClick={() => { setEditing(r); setDialogOpen(true); }} className="h-8 px-2">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" data-testid={`shg-toggle-${r.id}`}
          onClick={() => setConfirm({ row: r, action: r.status === "active" ? "deactivate" : "activate" })}
          className={`h-8 px-2 ${r.status === "active" ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
          {r.status === "active" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
        </Button>
      </div>
    )},
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={t.nav.shgs} subtitle="Self Help Groups under Village Organizations"
        testId="shgs-page-header" showSampleBadge={false}
        actions={
          <>
            <Select value={clfFilter} onValueChange={(v) => { setClfFilter(v); setVoFilter("all"); }}>
              <SelectTrigger className="h-9 w-44 bg-white" data-testid="shg-clf-filter">
                <SelectValue placeholder="Filter CLF" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All CLFs</SelectItem>
                {clfs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={voFilter} onValueChange={setVoFilter}>
              <SelectTrigger className="h-9 w-44 bg-white" data-testid="shg-vo-filter">
                <SelectValue placeholder="Filter VO" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All VOs</SelectItem>
                {filteredVoList.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs text-emerald-900/70">
              <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} data-testid="toggle-include-inactive" />
              Show inactive
            </label>
            <Button data-testid="shg-add-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              disabled={vos.length === 0}
              className="bg-[#0F4C3A] text-white hover:bg-emerald-900">
              <Plus className="mr-1.5 h-4 w-4" />Add SHG
            </Button>
          </>
        }
      />

      {vos.length === 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          Please register at least one VO before adding Self Help Groups.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">Loading…</div>
      ) : (
        <DataTable columns={columns} rows={rows}
          searchKeys={["name", "code", "village", "contact_person", "phone"]}
          testId="shgs-table"
          emptyLabel="No SHGs yet. Click “Add SHG” to register one under a VO." />
      )}

      <EntityDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editing ? "Edit SHG" : "Register new SHG"}
        fields={editing ? editFields : fields}
        initialValues={editing ? { whatsapp_consent: "no", ...editing } : { status: "active", whatsapp_consent: "no" }}
        validate={validateConsent}
        onSubmit={editing ? handleUpdate : handleCreate}
        submitLabel={editing ? "Save changes" : "Create SHG"}
        testIdPrefix="shg-dialog"
      />
      <ConfirmDialog
        open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.action === "deactivate" ? "Deactivate this SHG?" : "Reactivate this SHG?"}
        description={confirm?.action === "deactivate"
          ? `“${confirm?.row?.name}” will be hidden from active lists.`
          : `“${confirm?.row?.name}” will become active again.`}
        confirmLabel={confirm?.action === "deactivate" ? "Deactivate" : "Reactivate"}
        destructive={confirm?.action === "deactivate"}
        testId="shg-confirm"
        onConfirm={async () => { await toggleStatus(confirm.row); setConfirm(null); }}
      />
    </div>
  );
};

export default ShgsPage;
